/**
 * The catalog illustration — the one upload in this app, and the one **public** object in
 * the whole product's Storage besides a global's render.
 *
 * A photograph of the made object, shown in the store's grid and the Studio's picker before
 * anyone is signed in. Nothing family-private is reachable from here: the path is
 * `mediums/{id}/illustration.{ext}`, which the rules open to the world for reading and close
 * to everyone without the `admin` claim for writing.
 *
 * The type and size are checked with the same `checkIllustration` the path helper ships with,
 * before the bytes move, so a refusal costs nothing.
 */

import { Button, FramedWindow } from '@atlas-nook/design-system';
import { checkIllustration, illustrationUrl } from '@atlas-nook/firestore-store/client';
import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';

import { storage } from '../firebase.js';
import { clearIllustration, uploadIllustration } from '../data/writes.js';
import { ADMIN_STRINGS as S } from '../strings.js';

export function IllustrationSlot({
  mediumId,
  path,
}: {
  mediumId: string;
  path: string | undefined;
}): JSX.Element {
  const input = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    if (path === undefined) {
      setUrl(null);
      return;
    }
    let live = true;
    illustrationUrl(storage(), path)
      .then((next) => {
        if (live) setUrl(next);
      })
      .catch(() => {
        if (live) setUrl(null);
      });
    return () => {
      live = false;
    };
  }, [path]);

  async function onPick(file: File | undefined): Promise<void> {
    if (file === undefined) return;

    const refusal = checkIllustration(file);
    if (refusal !== null) {
      setError(
        refusal === 'wrong-type' ? S.medium.illustrationWrongType : S.medium.illustrationTooLarge,
      );
      return;
    }

    setError(null);
    setWorking(true);
    try {
      await uploadIllustration(mediumId, file);
    } catch {
      setError(S.medium.illustrationFailed);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 360 }}>
      <FramedWindow
        caption={S.medium.illustration}
        aspectRatio="3 / 2"
        state={path === undefined ? 'empty' : 'filled'}
      >
        {url === null ? (
          <span className="cap">{S.medium.illustrationNone}</span>
        ) : (
          <img src={url} alt="" style={{ objectFit: 'cover' }} />
        )}
      </FramedWindow>

      <p className="note">{S.medium.illustrationHelp}</p>
      {/* `.cost`, not `.cap`: a Storage path is case-sensitive, and `.cap` uppercases. */}
      {path !== undefined && <p className="cost">{S.medium.illustrationPath(path)}</p>}

      <div className="btnrow">
        <Button tone="secondary" size="sm" working={working} onClick={() => input.current?.click()}>
          {path === undefined ? S.medium.illustrationAdd : S.medium.illustrationReplace}
        </Button>
        {path !== undefined && (
          <Button tone="quiet" size="sm" onClick={() => void clearIllustration(mediumId)}>
            {S.medium.illustrationRemove}
          </Button>
        )}
      </div>

      {error !== null && (
        <p className="note" style={{ color: 'var(--danger)' }} role="alert">
          {error}
        </p>
      )}

      {/* Visually hidden, but not nameless: a file input with no label is the one thing axe
          calls critical. */}
      <input
        ref={input}
        type="file"
        className="vh"
        aria-label={S.medium.illustrationChoose}
        accept="image/png,image/jpeg,image/webp"
        onChange={(event) => void onPick(event.target.files?.[0])}
      />
    </div>
  );
}
